import React, { useMemo, useState } from "react";
import { SearchInput, DashedCtaButton, Eyebrow, SharedChip, GLYPH } from "./v2";

// Club Radar lists sidebar (desktop, frames 1a/1b/1c — V2 rebuild, 1:1 from the
// artboard). MY LISTS (My Radar first, then custom lists with a "Shared" chip +
// count) and a SHARED WITH <CLUB> section (colleagues' lists shared with me,
// showing the creator's first name). Search filters both sections by name.
// "+ New list" (dashed CTA) at the foot. Selection is the id.
//
// Artboard values (DESIGN-SPEC 1a): width 264, pad 14px 12px, gap 2; search h36
// bg #F8FAFC; eyebrows 10.5/700 .08em #9CA3AF; rows pad 9px 10px r10 — active
// bg #E8F1FF fg #2283FF 13.5/700 (count #1B6FE0, Shared pill white-on-tint),
// inactive 13.5/500 #111827 (count #9CA3AF); creator name 11px #9CA3AF.
// Presentation only — same client-side name filter, same handlers.

// "Jonas Weber" → "Jonas W." (artboard 1a shared-section creator format);
// single-word names render as-is.
const creatorShort = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const ListRow = ({ list, active, onSelect, creator }) => (
  <button
    type="button"
    onClick={() => onSelect(list.id)}
    className="w-full text-left cursor-pointer"
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "9px 10px",
      borderRadius: 10,
      background: active ? "#E8F1FF" : "transparent",
      border: "none",
      marginBottom: 2,
    }}
  >
    <span
      style={{
        minWidth: 0,
        flex: 1,
        fontSize: 13.5,
        fontWeight: active ? 700 : 500,
        color: active ? "#2283FF" : "#111827",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {list.name}
    </span>
    {list.share_tier && list.share_tier !== "personal" && (
      <SharedChip onTint={active}>Shared</SharedChip>
    )}
    {creator && (
      <span style={{ fontSize: 11, color: "#9CA3AF", flexShrink: 0 }}>{creator}</span>
    )}
    <span style={{ fontSize: 12, fontWeight: 600, color: active ? "#1B6FE0" : "#9CA3AF", flexShrink: 0 }}>
      {list.entry_count ?? 0}
    </span>
  </button>
);

const ClubListsSidebar = ({ myLists = [], sharedLists = [], selectedList, onSelect, onNewList, clubName }) => {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const my = useMemo(
    () => myLists.filter((l) => !query || String(l.name || "").toLowerCase().includes(query)),
    [myLists, query]
  );
  const shared = useMemo(
    () => sharedLists.filter((l) => !query || String(l.name || "").toLowerCase().includes(query)),
    [sharedLists, query]
  );

  return (
    <div
      style={{
        width: 264,
        flexShrink: 0,
        borderRight: "1px solid #E5E7EB",
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        padding: "14px 12px",
        gap: 2,
        boxSizing: "border-box",
      }}
    >
      <SearchInput
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search radar"
        height={36}
        style={{ marginBottom: 10 }}
      />

      <Eyebrow style={{ padding: "4px 10px 6px" }}>My lists</Eyebrow>
      {my.length === 0 && (
        <div style={{ fontSize: 11.5, color: "#9CA3AF", padding: "2px 10px 6px" }}>No lists</div>
      )}
      {my.map((l) => (
        <ListRow key={l.id} list={l} active={l.id === selectedList} onSelect={onSelect} />
      ))}

      {shared.length > 0 && (
        <>
          <Eyebrow style={{ padding: "14px 10px 6px" }}>Shared with {clubName || "your club"}</Eyebrow>
          {shared.map((l) => (
            <ListRow
              key={l.id}
              list={l}
              active={l.id === selectedList}
              onSelect={onSelect}
              creator={l.created_by ? creatorShort(l.created_by.name) : null}
            />
          ))}
        </>
      )}

      <div style={{ flex: 1 }} />
      <DashedCtaButton onClick={onNewList} height={38} radius={10} fontSize={13}>
        {GLYPH.plus} New list
      </DashedCtaButton>
    </div>
  );
};

export default ClubListsSidebar;
